import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, Button, LinearProgress } from '@mui/material';
import { useAuth } from '../context/AuthContext';

const initCountdown = 10; // seconds
const AccessDeniedPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [countdown, setCountdown] = useState(initCountdown);

    // This effect handles the entire timer and redirect logic.
    useEffect(() => {
        // When the countdown reaches zero, perform the redirect.
        if (countdown === 0) {
            navigate('/login', { replace: true }); // replace: true prevents user from clicking "back" to this page
            return;
        }

        // Set up a timer to decrement the countdown every second.
        const timerId = setInterval(() => {
            setCountdown(prevCountdown => prevCountdown - 1);
        }, 1000);

        // This is the cleanup function. It runs if the user navigates away
        // before the timer finishes, preventing errors.
        return () => {
            clearInterval(timerId);
        };
    }, [countdown, navigate]); // The effect re-runs whenever `countdown` or `navigate` changes.

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
                backgroundColor: '#f4f6f8'
            }}
        >
            <Paper sx={{ p: 4, textAlign: 'center', maxWidth: '550px', width: '100%' }}>
                <Typography variant="h4" color="error" gutterBottom>
                    Access Denied
                </Typography>

                {user && (
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Welcome, {user.name}. Your account is authenticated but does not have the required roles to access this application.
                    </Typography>
                )}

                <Typography variant="body2" sx={{ mb: 3 }}>
                    Please contact your system administrator if you believe this is an error.
                </Typography>

                {/* Provide a clear message about the redirect */}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Redirecting to login page in {countdown} seconds...
                </Typography>

                {/* A visual progress bar for better UX */}
                <LinearProgress
                    variant="determinate"
                    value={(countdown / initCountdown) * 100}
                    sx={{ mb: 3 }}
                />

                {/* Allow the user to log out immediately if they don't want to wait */}
                <form action="/logout" method="post">
                    <Button variant="contained" color="primary" type="submit">
                        Logout Now
                    </Button>
                </form>
            </Paper>
        </Box>
    );
};

export default AccessDeniedPage;